import qs from "qs"; // For query string manipulation
import CryptoJS from "crypto-js"; // For encryption and decryption
import * as types from "../Actions/ActionTypes"; // Redux action types
import * as urls from "../../Uintls/Urls";
import {
  clearAuthStorage,
  getFronteggAccessToken,
  redirectToFronteggLogout,
} from "../../authSession";
import { getExportFileName } from "Utils/commonUtils";

let backendTokenSyncPromise = null;

const withBackendAuth = (headers = {}) => {
  const customHeaders = { ...headers };
  const auth = localStorage.getItem("token");
  if (auth) {
    customHeaders.authorization = auth;
  }
  return customHeaders;
};

const withBackendFronteggAuth = (headers = {}) => {
  const customHeaders = { ...headers };
  const accessToken = getFronteggAccessToken();
  if (accessToken) {
    customHeaders.authorization = `BearerLogin ${accessToken}`;
  }
  return customHeaders;
};

export const syncBackendToken = async (dispatch) => {
  const accessToken = getFronteggAccessToken();

  if (!accessToken) {
    throw new Error("Missing Frontegg access token");
  }

  if (!backendTokenSyncPromise) {
    backendTokenSyncPromise = fetch(urls.FRONTEGG_URL, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        authorization: `BearerLogin ${accessToken}`,
      },
    })
      .then(async (res) => {
        const data = await res.json();

        if (!res.ok || data?.status === false) {
          throw data;
        }

        if (dispatch) {
          dispatch({ type: types.SET_LOGIN, data });
        } else if (data?.token) {
          const token = data.token.toLowerCase().startsWith("bearer ")
            ? data.token
            : `bearer ${data.token}`;
          localStorage.setItem("token", token);
        }

        return data;
      })
      .finally(() => {
        backendTokenSyncPromise = null;
      });
  }

  return backendTokenSyncPromise;
};

const fetchWithBackendAuth = async (url, options, dispatch, retryOn401 = true) => {
  const res = await fetch(url, {
    ...options,
    headers: withBackendAuth(options.headers),
  });

  if (res.status !== 401 || !retryOn401 || !dispatch) {
    return res;
  }

  try {
    await syncBackendToken(dispatch);
    return fetch(url, {
      ...options,
      headers: withBackendAuth(options.headers),
    });
  } catch (error) {
    clearAuthStorage();
    dispatch({ type: types.AUTH_LOGOUT });
    redirectToFronteggLogout();
    return res;
  }
};

const fetchWithBackendAuthApiLogs = async (url, options, dispatch, retryOn401 = true) => {
  const res = await fetch(url, {
    ...options,
    headers: withBackendFronteggAuth(options.headers),
  });

  if (res.status !== 401 || !retryOn401 || !dispatch) {
    return res;
  }

  try {
    await syncBackendToken(dispatch);
    return fetch(url, {
      ...options,
      headers: withBackendFronteggAuth(options.headers),
    });
  } catch (error) {
    clearAuthStorage();
    dispatch({ type: types.AUTH_LOGOUT });
    redirectToFronteggLogout();
    return res;
  }
};

function checkHTTPStatus(status) {
  return status === 401;
}

// Cache for storing API responses
export const cache = new Map();
export const CACHE_EXPIRATION_TIME = 60 * 60 * 1000; // 1 hour

// Function to make POST API calls
export const post = async ({
  url,
  success,
  failure,
  dispatch,
  body,
  auth,
  payment,
}) => {
  let data;
  const bodyValue = body;
  const clientId = localStorage.getItem("clientId")
    ? localStorage.getItem("clientId")
    : null;
  if (!auth) {
    bodyValue.ClientId = clientId;
  }
  try {
    let mainBody = "";
    if (bodyValue === "") {
      mainBody = "";
    } else {
      mainBody = JSON.stringify(bodyValue);
    }
    const customHeaders = {
      "Content-Type": "application/json",
    };
    const requestHeaders = payment === undefined
      ? withBackendAuth(customHeaders)
      : customHeaders;
    const res = await fetchWithBackendAuth(url, {
      method: "POST",
      headers: requestHeaders,
      body: mainBody,
    }, dispatch, payment === undefined);
    data = await res.json();
    checkHTTPStatus(res.status);
    dispatch({ type: success, data });
  } catch (e) {
    if (failure) {
      dispatch({ type: failure, data });
    }
  }
};

// Function to make POST API calls with caching
export const postApiLogs = async ({
  url,
  success,
  failure,
  dispatch,
  body,
  payment,
  noCache = false,
}) => {
  let data;
  const bodyValue = body;

  const createCacheKey = (params) => {
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== undefined && value !== null && value !== ""
      )
    );
    return JSON.stringify(filteredParams);
  };

  // Generate cache key
  const cacheKey = createCacheKey({ url, body });

  // Check if response exists in cache and is still valid
  if (cache.has(cacheKey) && !noCache) {
    const { timestamp, response } = cache.get(cacheKey);
    if (Date.now() - timestamp < CACHE_EXPIRATION_TIME) {
      dispatch({ type: success, data: response });
      return response; // Return cached response
    }
    cache.delete(cacheKey); // Remove expired cache
  } else if (noCache) {
    cache.delete(cacheKey);
  }

  try {
    const mainBody = bodyValue ? JSON.stringify(bodyValue) : "";
    const customHeaders = {
      "Content-Type": "application/json",
    };
    const requestHeaders = payment === undefined
      ? withBackendAuth(customHeaders)
      : customHeaders;
    const res = await fetchWithBackendAuth(url, {
      method: "POST",
      headers: requestHeaders,
      body: mainBody,
    }, dispatch, payment === undefined);
    data = await res.json();
    checkHTTPStatus(res.status);

    // Cache the response
    cache.set(cacheKey, { timestamp: Date.now(), response: data });

    dispatch({ type: success, data });
  } catch (e) {
    if (failure) {
      dispatch({ type: failure, data });
    }
  }
};

export const postLogin = async ({
  url,
  success,
  failure,
  dispatch,
  body,

}) => {
  const myHeaders = new Headers();
  myHeaders.append("Content-Type", "application/json");
  let data;
  const bodyValue = body;
  // const clientId = localStorage.getItem("clientId")
  //   ? localStorage.getItem("clientId")
  //   : null;
  // if (!auth) {
  //   bodyValue.ClientId = clientId;
  // }
  try {
    let mainBody = "";
    if (bodyValue === "") {
      mainBody = "";
    } else {
      mainBody = JSON.stringify(bodyValue);
    }
    // const customHeaders = {
    //   "Content-Type": "application/json",
    // };
    // const auth = localStorage.getItem("token");
    // if (auth && payment === undefined) {
    //   customHeaders.authorization = "";
    // }
    const res = await fetch(url, {
      method: "POST",
      headers: myHeaders,
      body: mainBody,
    });
    data = await res.json();
    checkHTTPStatus(res.status);
    dispatch({ type: success, data });
  } catch (e) {
    if (failure) {
      dispatch({ type: failure, data });
    }
  }
};

export const postSync = async ({
  url,
  bannerNumber,
  success,
  failure,
  dispatch,
  body,
}) => {
  let data;
  try {
    const customHeaders = {
      "Content-Type": "application/json",
    };
    const res = await fetchWithBackendAuth(url, {
      method: "POST",
      headers: withBackendAuth(customHeaders),
      body: JSON.stringify(body),
    }, dispatch);
    data = await res.json();
    checkHTTPStatus(res.status);
    dispatch({ type: success, data, bannerNumber });
  } catch (e) {
    if (failure) {
      dispatch({ type: failure, data, bannerNumber });
    }
  }
};

export const getExport = async (url, body) => {
  let data;
  try {
    const customHeaders = {
      "Content-Type": "application/json",
    };
    let res = null;
    if (body) {
      res = await fetch(`${url}?${qs.stringify(body)}`, {
        method: "get",
        headers: withBackendAuth(customHeaders),
      });
    } else {
      res = await fetch(url, {
        method: "get",
        headers: withBackendAuth(customHeaders),
      });
    }
    const responseText = await res.json();
    checkHTTPStatus(res.status);
    const encryptionKey = process.env.REACT_APP_ENCRYPTION_ENVIRONMENT;
    const secretKey = process.env.REACT_APP_ENCRIPTION_KEY;
    const bytes1 = CryptoJS.AES.decrypt(responseText.data, secretKey);
    const decryptedData1 = encryptionKey === "NO"
      ? responseText.data
      : bytes1.toString(CryptoJS.enc.Utf8);
    data = {
      ...responseText,
      data: JSON.parse(decryptedData1),
    };
    return data;
  } catch (e) {
    if (e) {
      throw e;
    }
  }
};

export const getExportfromFrontegg = async (url, body) => {
  let fileName = getExportFileName("RTP");
  try {
    const customHeaders = {
      "Content-Type": "application/json",
    };

    const requestHeaders = withBackendFronteggAuth(customHeaders);

    const requestUrl = body
      ? `${url}?${qs.stringify(body)}`
      : url;

    const res = await fetch(requestUrl, {
      method: "GET",
      headers: requestHeaders,
    });

    if (!res.ok) {
      throw new Error(`Export failed with status ${res.status}`);
    }
    const blob = await res.blob();
    const disposition = res.headers.get("content-disposition") ?? "";
    const match = disposition.match(/filename="?([^"]+)"?/i);
    const downloadFileName = match?.[1] ?? fileName;
    const blobUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = downloadFileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(blobUrl);
    return blob;
  } catch (e) {
    throw e;
  }
};

export const getExport1 = async (url, body) => {
  let data;
  try {
    const customHeaders = {
      "Content-Type": "application/json",
    };
    let res = null;
    if (body) {
      res = await fetch(`${url}?${qs.stringify(body)}`, {
        method: "get",
        headers: withBackendAuth(customHeaders),
      });
    } else {
      res = await fetch(url, {
        method: "get",
        headers: withBackendAuth(customHeaders),
      });
    }
    data = await res.json();
    checkHTTPStatus(res.status);
    return data;
  } catch (e) {
    if (e) {
      throw e;
    }
  }
};

export const getExportJsonData = async (url, body) => {
  let data;
  try {
    const customHeaders = {
      "Content-Type": "application/json",
    };
    let res = null;
    if (body) {
      res = await fetch(`${url}?${qs.stringify(body)}`, {
        method: "get",
        headers: withBackendFronteggAuth(customHeaders),
      });
    } else {
      res = await fetch(url, {
        method: "get",
        headers: withBackendFronteggAuth(customHeaders),
      });
    }
    data = await res.json();
    checkHTTPStatus(res.status);
    return data;
  } catch (e) {
    if (e) {
      throw e;
    }
  }
};

export const getExportJsonDataACH = async (url, body) => {
  let data;
  try {
    const customHeaders = {
      "Content-Type": "application/json",
    };
    let res = null;
    if (body) {
      res = await fetch(`${url}`, {
        method: "post",
        headers: withBackendAuth(customHeaders),
        body: JSON.stringify(body)
      });
    } else {
      res = await fetch(url, {
        method: "post",
        headers: withBackendAuth(customHeaders),
        body: JSON.stringify(body)
      });
    }
    data = await res.json();
    checkHTTPStatus(res.status);
    return data;
  } catch (e) {
    if (e) {
      throw e;
    }
  }
};

export const getUnmaskedJsonData = async (url, body) => {
  try {
    const customHeaders = {
      "Content-Type": "application/json",
    };

    const res = await fetch(url, {
      method: "POST",
      headers: withBackendFronteggAuth(customHeaders),
      body: JSON.stringify(body),
    });

    const data = await res.json();
    checkHTTPStatus(res.status);

    return data;
  } catch (e) {
    throw e;
  }
};

export const getUnmaskedJsonDataACH = async (url, body) => {
  try {
    const customHeaders = {
      "Content-Type": "application/json",
    };

    const res = await fetch(url, {
      method: "POST",
      headers: withBackendFronteggAuth(customHeaders),
      body: JSON.stringify(body),
    });

    const data = await res.json();
    checkHTTPStatus(res.status);

    return data;
  } catch (e) {
    throw e;
  }
};

// Check if the response is already cached; if so, return the cached response. Otherwise, call the API and return the API response.

export const getWithCache = async ({
  url, success, failure, dispatch, body, noCache = false,
}) => {
  let data;
  let bodyValue = body;

  const createCacheKey = params => {
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== undefined && value !== null && value !== ""
      )
    );
    return JSON.stringify(filteredParams);
  };

  // Generate cache key
  const cacheKey = createCacheKey({ url, body });

  // Check if response exists in cache and is still valid
  if (cache.has(cacheKey) && !noCache) {
    const { timestamp, response } = cache.get(cacheKey);
    if (Date.now() - timestamp < CACHE_EXPIRATION_TIME) {
      dispatch({ type: success, data: response });
      return response; // Return cached response
    }
    cache.delete(cacheKey); // Remove expired cache
  } else if (noCache) {
    cache.delete(cacheKey);
  }

  try {
    const customHeaders = {
      "Content-Type": "application/json",
    };
    if (body === "default" || !body) {
      bodyValue = "";
    }

    const finalUrl = bodyValue ? `${url}?${qs.stringify(bodyValue)}` : url;

    const res = await fetchWithBackendAuth(finalUrl, {
      method: "GET",
      headers: withBackendAuth(customHeaders),
    }, dispatch);

    data = await res.json();
    data.initialCall = true;
    checkHTTPStatus(res.status);

    // Cache the response
    cache.set(cacheKey, { timestamp: Date.now(), response: data });

    dispatch({ type: success, data });
    return data;
  } catch (e) {
    if (failure) {
      dispatch({ type: failure, data });
    }
  }
};

export const getWithCacheApiLogs = async ({
  url, success, failure, dispatch, body, noCache = false,
}) => {
  let data;
  let bodyValue = body;

  const createCacheKey = params => {
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== undefined && value !== null && value !== ""
      )
    );
    return JSON.stringify(filteredParams);
  };

  // Generate cache key
  const cacheKey = createCacheKey({ url, body });

  // Check if response exists in cache and is still valid
  if (cache.has(cacheKey) && !noCache) {
    const { timestamp, response } = cache.get(cacheKey);
    if (Date.now() - timestamp < CACHE_EXPIRATION_TIME) {
      dispatch({ type: success, data: response });
      return response; // Return cached response
    }
    cache.delete(cacheKey); // Remove expired cache
  } else if (noCache) {
    cache.delete(cacheKey);
  }

  try {
    const customHeaders = {
      "Content-Type": "application/json",
    };
    if (body === "default" || !body) {
      bodyValue = "";
    }

    const finalUrl = bodyValue ? `${url}?${qs.stringify(bodyValue)}` : url;

    const res = await fetchWithBackendAuthApiLogs(finalUrl, {
      method: "GET",
      headers: withBackendFronteggAuth(customHeaders),
    }, dispatch);

    data = await res.json();
    data.initialCall = true;
    checkHTTPStatus(res.status);

    // Cache the response
    cache.set(cacheKey, { timestamp: Date.now(), response: data });

    dispatch({ type: success, data });
    return data;
  } catch (e) {
    if (failure) {
      dispatch({ type: failure, data });
    }
  }
};


export const exportApiLogs = async (url, body) => {
  let fileName = getExportFileName(body.groupName);
  try {
    const customHeaders = {
      "Content-Type": "application/json",
    };
    const requestHeaders = withBackendAuth(customHeaders);
    const res = await fetchWithBackendAuth(url, {
      method: "POST",
      headers: requestHeaders,
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      throw new Error(`Export failed with status ${res.status}`);
    }
    const blob = await res.blob();
    const disposition = res.headers.get("content-disposition") ?? "";
    const match = disposition.match(/filename="?([^"]+)"?/i);
    const downloadFileName = match?.[1] ?? fileName;
    const blobUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = downloadFileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(blobUrl);
    return blob;
  } catch (e) {
    throw e;
  }
};

export const get = async ({
  url, success, failure, dispatch, body,
}) => {
  let data;
  let bodyValue = body;
  try {
    const customHeaders = {
      "Content-Type": "application/json",
    };
    const auth = localStorage.getItem("token");
    if (auth) {
      customHeaders.authorization = auth;
    }
    if (body === "default" || !body) {
      bodyValue = "";
    }
    let finalurl = "";
    if (bodyValue === "") {
      finalurl = `${url}`;
    } else {
      finalurl = `${url}?${qs.stringify(bodyValue)}`;
    }
    const res = await fetchWithBackendAuth(finalurl, {
      method: "get",
      headers: withBackendAuth(customHeaders),
    }, dispatch);
    data = await res.json();
    data.initialCall = true;
    checkHTTPStatus(res.status);
    if (!res.ok || data?.status === false) {
      if (failure) {
        dispatch({ type: failure, data });
      }
      throw data;
    }
    dispatch({ type: success, data });
    return data;
  } catch (e) {
    if (failure) {
      dispatch({ type: failure, data: data || e });
    }
    throw e;
  }
};

export const getApiPorject = async ({
  url,
  success,
  failure,
  dispatch,
  body,
}) => {
  let data;
  let bodyValue = body;

  try {
    const customHeaders = {
      "Content-Type": "application/json",
    };

    const auth = localStorage.getItem("fronteggAccessToken");

    if (auth) {
      customHeaders.Authorization = auth;
    }

    if (body === "default" || !body) {
      bodyValue = "";
    }

    let finalurl = "";

    if (bodyValue === "") {
      finalurl = `${url}`;
    } else {
      finalurl = `${url}?${qs.stringify(bodyValue)}`;
    }

    const res = await fetch(
      finalurl,
      {
        method: "GET",
        headers: customHeaders,
      }
    );

    data = await res.json();

    data.initialCall = true;

    checkHTTPStatus(res.status);

    dispatch({
      type: success,
      data,
    });

    return data;
  } catch (e) {
    console.error(e);

    if (failure) {
      dispatch({
        type: failure,
        data,
      });
    }
  }
};

export const getFrontagg = async ({
  url,
  success,
  failure,
  dispatch,
  body,
  userTokenData,
}) => {
  let data;
  let bodyValue = body;
  let failureDispatched = false;
  try {
    const customHeaders = {
      "Content-Type": "application/json",
    };
    const auth = `BearerLogin ${userTokenData}`;
    if (auth) {
      customHeaders.authorization = auth;
    }
    if (body === "default" || !body) {
      bodyValue = "";
    }
    let finalurl = "";
    if (bodyValue === "") {
      finalurl = `${url}`;
    } else {
      finalurl = `${url}?${qs.stringify(bodyValue)}`;
    }
    const res = await fetch(finalurl, {
      method: "get",
      headers: customHeaders,
    });
    data = await res.json();
    data.initialCall = true;
    checkHTTPStatus(res.status);
    if (!res.ok || data?.status === false) {
      if (failure) {
        dispatch({ type: failure, data });
        failureDispatched = true;
      }
      throw data;
    }
    dispatch({ type: success, data });
    return data;
  } catch (e) {
    if (failure && !failureDispatched) {
      dispatch({ type: failure, data: data || e });
    }
    throw e;
  }
};

export const put = async ({
  url, success, failure, dispatch, body,
}) => {
  let data;
  const bodyValue = body;
  try {
    const customHeaders = {
      "Content-Type": "application/json",
    };
    const res = await fetchWithBackendAuth(url, {
      method: "PUT",
      headers: withBackendAuth(customHeaders),
      body: JSON.stringify(bodyValue),
    }, dispatch);
    data = await res.json();
    checkHTTPStatus(res.status);
    dispatch({ type: success, data });
  } catch (e) {
    if (failure) {
      dispatch({ type: failure, data });
    }
  }
};

export const del = async ({
  url, success, failure, dispatch, body,
}) => {
  let data;
  try {
    const customHeaders = {
      "Content-Type": "application/json",
    };
    const res = await fetchWithBackendAuth(url, {
      method: "DELETE",
      headers: withBackendAuth(customHeaders),
      body: JSON.stringify(body),
    }, dispatch);
    data = await res.json();
    checkHTTPStatus(res.status);
    dispatch({ type: success, data });
  } catch (e) {
    if (failure) {
      dispatch({ type: failure, data });
    }
  }
};
